import Link from 'next/link';
import {
    IoBriefcaseOutline,
    IoCalendarOutline,
    IoEyeOffOutline,
    IoFileTrayOutline,
    IoIdCardOutline,
    IoPeopleOutline,
    IoQrCodeSharp,
    IoRocketOutline,
    IoSearchOutline,
} from 'react-icons/io5';
import { TbPlus } from 'react-icons/tb';

import { Anchor, Badge, Button, Group, RingProgress, SimpleGrid, Stack, Text } from '@mantine/core';
import { PublishButton } from 'src/frontend/components/company-profile/components/PublishButton';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import {
    CompanyProfile as CompanyProfileType,
    CompanySubscription,
    PermissionCompany,
    PublishedStatus,
    SubscriptionType,
} from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';
import { TeamHelpers } from 'helpers/TeamHelpers';
import { TeamUserHelpers } from 'helpers/TeamUserHelpers';

import { Avatar, AvatarGroup } from 'components/avatar/Avatar';
import { BoothNumber } from 'components/booth-number/BoothNumber';
import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { CompanyLogoFallback } from 'components/company-logo/CompanyLogoFallback';
import { SubscriptionBadge } from 'components/company-profile/components/SubscriptionBadge';
import { useCompanyEvent } from 'components/company-profile/hooks/use-company-event';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { useProfileProgress, useProgressItems } from 'components/company-profile/hooks/use-profile-progress';
import { Logo } from 'components/company-profile-teaser/components/Logo';
import { COMPONENT_PROGRESS_LEVELS } from 'components/component-progress/helpers/config';
import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';
import { PublishedBadge } from 'components/published-badge/PublishedBadge';
import { VerifiedBadge } from 'components/verified-badge/VerifiedBadge';
import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { useNotificationCounts } from 'hooks/use-inbox';
import { useQrScansByCompany } from 'hooks/use-qr-scans-by-company';
import { useTeam } from 'hooks/use-team';
import { NotificationService } from 'services/NotificationService';

import { DashboardActionRow, THUMBNAIL_SIZE } from './DashboardActionRow';

const NB_SHOWN_COMPANIES = 6;
const NB_SHOWN_EDITORS = 4;
const COMPANY_PROFILE_BENEFITS = [
    {
        icon: <IoIdCardOutline />,
        title: 'Build a complete company profile',
        description: 'Your digital business card, with everything behind it: products, services, and case studies.',
    },
    {
        icon: <IoSearchOutline />,
        title: 'Be discovered',
        description: 'A modern search experience for service providers and product manufacturers.',
    },
    {
        icon: <IoCalendarOutline />,
        title: 'Stay visible all year',
        description: 'Your profile keeps working for you long after the event closes.',
    },
];

const DashboardCompanyProfile = () => {
    const { companies } = useCurrentTeamCompanies();

    if (companies.length === 0) {
        return <CreateCompanyProfile />;
    }

    if (companies.length === 1) {
        return <SingleCompanyProfile company={companies[0]} />;
    }

    return <MultipleCompanyProfiles companies={companies} />;
};

const CreateCompanyProfile = () => (
    <DashboardPanel
        icon={<IoBriefcaseOutline />}
        color="violet"
        title="Create your company profile"
        rightSection={
            <Group gap="md">
                <Anchor component={Link} href={MarketingHelpers.urls.resourcepro.joinAsPro()} fw={500} fz="sm">
                    Learn More
                </Anchor>
                <Button
                    component={Link}
                    href={CompanyHelpers.urls.create()}
                    size="xs"
                    leftSection={<TbPlus size={14} strokeWidth={1.25} />}
                >
                    Join as a Pro
                </Button>
            </Group>
        }
    >
        <Text size="sm" c="dimmed">
            Set up your profile in minutes.
        </Text>

        <Stack gap={0} className={cxPanel.dividedList}>
            {COMPANY_PROFILE_BENEFITS.map((benefit) => (
                <DashboardActionRow
                    key={benefit.title}
                    icon={benefit.icon}
                    title={benefit.title}
                    description={benefit.description}
                />
            ))}
        </Stack>
    </DashboardPanel>
);

const SingleCompanyProfile = ({ company }: { company: CompanyProfileType }) => (
    <DashboardPanel
        header={<CompanyHeader company={company} />}
        shadedHeader
        rightSection={
            <Group gap="md">
                <Anchor component={Link} href={CompanyHelpers.urls.edit(company.slug)} fw={500} fz="sm">
                    Edit Profile
                </Anchor>
                <Anchor component={Link} href={CompanyHelpers.urls.view(company.slug)} fw={500} fz="sm">
                    View Profile
                </Anchor>
            </Group>
        }
    >
        <Stack gap={0} className={cxPanel.dividedList}>
            <CompanyProgress company={company} />
            <CompanyInbox company={company} />
            <CompanyQRCode company={company} />
            <CompanyEditors company={company} />
            <CompanyPremiumUpsell company={company} />
        </Stack>
    </DashboardPanel>
);

const MultipleCompanyProfiles = ({ companies }: { companies: CompanyProfileType[] }) => (
    <DashboardPanel
        icon={<IoBriefcaseOutline />}
        color="violet"
        title="Company Profiles"
        rightSection={
            <Anchor component={Link} href={CompanyHelpers.urls.manage()} fw={500} fz="sm">
                Manage Profiles
            </Anchor>
        }
    >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing={0} className={cxPanel.dividedGrid}>
            {companies.slice(0, NB_SHOWN_COMPANIES).map((company) => (
                <DashboardActionRow
                    key={company.id}
                    href={CompanyHelpers.urls.view(company.slug)}
                    thumbnail={
                        <CompanyLogo
                            logos={company.logos}
                            width={THUMBNAIL_SIZE}
                            fallback={<CompanyLogoFallback name={company.name} w={THUMBNAIL_SIZE} h={THUMBNAIL_SIZE} />}
                        />
                    }
                    title={company.name}
                    description={
                        company.status !== PublishedStatus.PUBLISHED && (
                            <PublishedBadge status={company.status} size="sm" style={{ marginTop: 2 }} />
                        )
                    }
                />
            ))}
        </SimpleGrid>
    </DashboardPanel>
);

const CompanyHeader = ({ company }: { company: CompanyProfileType }) => (
    <Group gap="md" wrap="nowrap">
        <Logo company={company} withBorder />
        <Stack gap={6} miw={0}>
            <Text fw={700} size="lg" lh={1.2}>
                {company.name}
                {company.verified && (
                    <VerifiedBadge compact companyName={company.name} style={{ marginLeft: 6, marginBottom: -1 }} />
                )}
            </Text>
            <Group gap={6}>
                {company.status !== PublishedStatus.PUBLISHED && <PublishedBadge status={company.status} size="sm" />}
                <SubscriptionBadge company={company} variant="light" />
            </Group>
        </Stack>
    </Group>
);

const CompanyProgress = ({ company }: { company: CompanyProfileType }) => {
    const { percentage } = useProfileProgress(company);

    if (percentage === null) {
        return null;
    }

    const isComplete = percentage === 100;

    return (
        <>
            {!isComplete && <FinishProfileRow company={company} percentage={percentage} />}
            {company.status === PublishedStatus.DRAFT && (
                <DashboardActionRow
                    icon={<IoEyeOffOutline />}
                    color="orange"
                    title="Publish your profile"
                    description="Your profile is not published yet."
                    rightSection={<PublishButton company={company} size="xs" />}
                />
            )}
            {company.status === PublishedStatus.REVIEW && (
                <DashboardActionRow
                    icon={<IoEyeOffOutline />}
                    color="orange"
                    title="Profile under review"
                    description="We will notify you once your profile has been published."
                />
            )}
        </>
    );
};

const FinishProfileRow = ({ company, percentage }: { company: CompanyProfileType; percentage: number }) => {
    const progressItems = useProgressItems(company);
    const { progress } = useProfileProgress(company);

    const nextItem = progressItems.find(({ value }) => progress.find((item) => item.key === value)?.progress === false);

    const { color } = COMPONENT_PROGRESS_LEVELS.find((level) => percentage < level.maxPercent)!;

    return (
        <DashboardActionRow
            color={color}
            icon={
                <RingProgress
                    size={36}
                    thickness={3}
                    roundCaps
                    sections={[{ value: percentage, color }]}
                    label={
                        <Text fz={9} fw={700} ta="center">
                            {percentage}%
                        </Text>
                    }
                />
            }
            title="Finish your profile"
            description={nextItem ? `Next: ${nextItem.label}` : 'A few details are still missing'}
            href={CompanyHelpers.urls.edit(company.slug)}
        />
    );
};

const CompanyInbox = ({ company }: { company: CompanyProfileType }) => {
    const { categoryCounts, isLoading } = useNotificationCounts(null, company.id);
    const { unread } = categoryCounts.all;

    if (isLoading) {
        return null;
    }

    return (
        <DashboardActionRow
            icon={<IoFileTrayOutline />}
            title="Inbox"
            description={
                unread
                    ? `${IntlHelpers.formatNumber(unread)} unread about this company`
                    : 'Customer questions, leads, and access requests land here'
            }
            onClick={() => NotificationService.openInbox({ category: null, company: company.id })}
            rightSection={<UnreadBadge count={unread} />}
        />
    );
};

const CompanyQRCode = ({ company }: { company: CompanyProfileType }) => {
    const { leads } = useQrScansByCompany({ companyId: company.id });
    const { event } = useCompanyEvent(company);

    if (event) {
        return null;
    }

    return (
        <DashboardActionRow
            icon={<IoQrCodeSharp />}
            title="Your QR codes"
            description="For your profile, collateral and products. Share them at events to collect leads"
            href={CompanyHelpers.urls.qr(company.slug)}
            rightSection={
                leads.length > 0 && (
                    <Text size="sm" c="dimmed">
                        {IntlHelpers.formatCount(leads.length, 'lead', 'leads')}
                    </Text>
                )
            }
        />
    );
};

const CompanyEditors = ({ company }: { company: CompanyProfileType }) => {
    const { team } = useTeam(company.team);

    if (!team) {
        return null;
    }

    const userIds = CollectionHelpers.ids(TeamUserHelpers.fromTeam(team), 'user');
    const hiddenCount = userIds.length - NB_SHOWN_EDITORS;

    return (
        <DashboardActionRow
            icon={<IoPeopleOutline />}
            title="Profile managers"
            description={
                userIds.length > 1
                    ? `Everyone on ${team.name} can manage this profile. Invite coworkers to help.`
                    : 'Only you so far. Invite coworkers to help manage this profile.'
            }
            href={TeamHelpers.urls.settings(team)}
            rightSection={
                <AvatarGroup spacing="xs">
                    {userIds.slice(0, NB_SHOWN_EDITORS).map((userId) => (
                        <Avatar key={userId} user={userId} size={32} tooltip />
                    ))}
                    {hiddenCount > 0 && (
                        <Text size="xs" c="dimmed" ml={6}>
                            +{hiddenCount}
                        </Text>
                    )}
                </AvatarGroup>
            }
        />
    );
};

const CompanyPremiumUpsell = ({ company }: { company: CompanyProfileType }) => {
    const { team } = useTeam(company.team);
    const canEdit = useProfilePermission(PermissionCompany.EDIT, company);

    const { subscription } = SubscriptionHelpers.getSubscriptionData(SubscriptionType.COMPANY, team?.subscriptions);

    if (!team || !canEdit || subscription === CompanySubscription.PREMIUM) {
        return null;
    }

    return (
        <DashboardActionRow
            icon={<IoRocketOutline />}
            title="Go Premium"
            description="See who scanned you and get boosted placement in search."
            href={MarketingHelpers.urls.resourcepro.pricing(CompanyHelpers.urls.upgrade(company.slug))}
        />
    );
};

const UnreadBadge = ({ count }: { count: number }) => (
    <Badge color={count ? 'red' : 'gray'} size="sm" variant={count ? 'filled' : 'light'}>
        {IntlHelpers.formatNumber(count)}
    </Badge>
);

export { DashboardCompanyProfile };
